import React, { useState } from 'react';
import { faPlus, faRefresh, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAtendimentos } from '../hooks/useAtendimentos';
import { useAuth } from '../hooks/useAuth';
import { useRoleSelection } from '../hooks/useRoleSelection';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import PatientQueueCard from '../components/PatientQueueCard';
import AddToQueueModal from '../components/AddToQueueModal';
import Alert from '../components/Alert';

const AtendimentosFila: React.FC = () => {
  const { currentOrganization } = useAuth();
  const { clearRoleSelection } = useRoleSelection();
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const organizationId = currentOrganization?.id || '';

  const { atendimentos, loading, error, addToQueue, callPatient, cancelAttendance, refetch } =
    useAtendimentos(organizationId);

  const handleAddToQueue = async (pacienteId: string, isPriority: boolean) => {
    setActionLoading('adding');
    try {
      const response = await addToQueue(pacienteId, isPriority);
      if (response.success) {
        setShowAddModal(false);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleCallPatient = async (atendimentoId: string) => {
    setActionLoading(atendimentoId);
    try {
      await callPatient(atendimentoId);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelAttendance = async (atendimentoId: string) => {
    setActionLoading(atendimentoId);
    try {
      await cancelAttendance(atendimentoId);
    } finally {
      setActionLoading(null);
    }
  };

  const waitingPatients = atendimentos.filter(
    a => !a.called_at && !a.finished_at && !a.canceled_at
  );
  const calledPatients = atendimentos.filter(a => a.called_at && !a.finished_at && !a.canceled_at);

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto space-y-6">
        <div className="flex justify-between items-start">
          <PageHeader
            title="Fila de Atendimento - Recepção"
            subtitle="Gerencie a fila de pacientes aguardando atendimento"
          />
          <Button
            variant="outline"
            leftIcon={faArrowLeft}
            onClick={clearRoleSelection}
            disabled={!!actionLoading}
            size="sm"
          >
            Trocar Papel
          </Button>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            <Button
              variant="primary"
              leftIcon={faPlus}
              onClick={() => setShowAddModal(true)}
              disabled={!!actionLoading}
            >
              Adicionar Paciente
            </Button>
            <Button
              variant="secondary"
              leftIcon={faRefresh}
              onClick={refetch}
              loading={loading}
              disabled={!!actionLoading}
            >
              Atualizar
            </Button>
          </div>

          <div className="text-sm text-gray-600">
            <span className="font-medium">{waitingPatients.length}</span> aguardando •
            <span className="font-medium text-blue-600 ml-1">{calledPatients.length}</span> chamados
          </div>
        </div>

        {error && <Alert variant="error" title="Erro ao carregar fila" message={error} />}

        {calledPatients.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pacientes Chamados</h3>
            <div className="grid gap-4">
              {calledPatients.map(atendimento => (
                <PatientQueueCard
                  key={atendimento.id}
                  atendimento={atendimento}
                  onCancel={handleCancelAttendance}
                  showActions={{ call: false, finish: false, cancel: true }}
                  loading={actionLoading}
                />
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Fila de Espera ({waitingPatients.length} pacientes)
          </h3>

          {loading && !atendimentos.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando fila de atendimento...</p>
            </div>
          ) : waitingPatients.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">Nenhum paciente na fila de espera</p>
              <Button variant="primary" leftIcon={faPlus} onClick={() => setShowAddModal(true)}>
                Adicionar Primeiro Paciente
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {waitingPatients.map((atendimento, index) => (
                <div key={atendimento.id} className="relative">
                  <div className="absolute -left-4 top-4 bg-blue-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                    {index + 1}
                  </div>
                  <PatientQueueCard
                    atendimento={atendimento}
                    onCall={handleCallPatient}
                    onCancel={handleCancelAttendance}
                    showActions={{ call: true, finish: false, cancel: true }}
                    loading={actionLoading}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <AddToQueueModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddToQueue}
          loading={actionLoading === 'adding'}
          organizationId={organizationId}
        />
      </div>
    </main>
  );
};

export default AtendimentosFila;
